import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { processFolderService } from "@/services/process-folder/process-folder.service";
import type {
  CreateProcessFolderRequest,
  UpdateProcessFolderRequest,
  ProcessFolder,
} from "@/types/process-folder.types";
import { toast } from "sonner";

export const useGetAllProcessFolders = () =>
  useQuery<ProcessFolder[], Error>({
    queryKey: ["process-folders"],
    queryFn: processFolderService.getAllProcessFolders,
  });

export const useGetProcessFolderById = (id: string) =>
  useQuery<ProcessFolder, Error>({
    queryKey: ["process-folders", id],
    queryFn: () => processFolderService.getProcessFolderById(id),
    enabled: !!id,
  });

export const useCreateProcessFolder = () => {
  const queryClient = useQueryClient();
  return useMutation<ProcessFolder, Error, CreateProcessFolderRequest>({
    mutationFn: processFolderService.createProcessFolder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["process-folders"] });
      toast.success("Process folder created successfully");
    },
    onError: () => {
      toast.error("Failed to create process folder");
    },
  });
};

export const useUpdateProcessFolder = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ProcessFolder,
    Error,
    { id: string; data: UpdateProcessFolderRequest }
  >({
    mutationFn: ({ id, data }) => processFolderService.updateProcessFolder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["process-folders"] });
      toast.success("Process folder updated successfully");
    },
    onError: () => {
      toast.error("Failed to update process folder");
    },
  });
};

export const useDeleteProcessFolder = () => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, string>({
    mutationFn: processFolderService.deleteProcessFolder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["process-folders"] });
      toast.success("Process folder deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete process folder");
    },
  });
};

